import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError, errorMessages } from '../../api/client'
import { moduleAlarmsQuery } from '../../api/settingsModuleAlarms'
import {
  createProfile,
  isPrinteengTab,
  printeengShellQuery,
  profileSettings,
  profilesPageQuery,
  profilesTableQuery,
  removeProfile,
  updateProfile,
  type Company,
  type PrinteengTab,
  type ProfileRow,
} from '../../api/settingsPrinteeng'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { ModuleAlarmsEditor } from '../moduleAlarms/ModuleAlarmsEditor'

/**
 * Settings → Printeeng: `printeeng_settings/tabList.php` + Profiles
 * (`profiles/index.php`, `newProfileModal.php`, `actionTabs.php`,
 * `settings.php`) + Alarms (`alarms/index.php`), element cu element.
 */
export function PrinteengSettingsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(printeengShellQuery())

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isPrinteengTab(tab)) return
    const first = shell.data.tabs[0]
    if (first) void navigate({ to: '/settings/printeeng/$tab', params: { tab: first.id } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!shell.data) return <Spinner />
  const current = isPrinteengTab(tab) ? tab : (shell.data.tabs[0]?.id ?? '')

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          <li className="menu-header">Printeeng</li>
          {shell.data.tabs.map((item) => (
            <SidebarTab key={item.id} tab={item} current={current} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </aside>
      <div className="page-content-wrapper">
        {current === 'profiles' && <ProfilesPanel />}
        {current === 'alarms' && <AlarmsPanel />}
      </div>
    </>
  )
}

function SidebarTab({ tab, current }: { tab: PrinteengTab; current: string }) {
  const navigate = useNavigate()
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    void navigate({ to: '/settings/printeeng/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={tab.id === current ? 'menu-tab active' : 'menu-tab'}>
      <a href={'/app/settings/printeeng/' + tab.id} onClick={onClick}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

const PERIODS = [
  ['0', 'Week'],
  ['1', 'Month'],
  ['2', 'Year'],
] as const

function ProfilesPanel() {
  const client = useQueryClient()
  const page = useQuery(profilesPageQuery())
  const table = useQuery(profilesTableQuery())
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState<number>(0)
  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'printeeng', 'profiles'] })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!page.data || !table.data) return <Spinner />

  return (
    <div id="content">
      <header className="panel-heading" style={{ borderBottom: 0 }}>
        <h3>
          <strong>Profiles</strong>
        </h3>
        <label className="color">profiles settings</label>
      </header>
      <div className="dt-buttons">
        <button className="btn btn-success qr" onClick={() => setOpen(true)}>
          <i className="fas fa-plus"></i> Create profile
        </button>
      </div>
      <table className="table table-striped table-hover table-bordered dataTableM" width="100%" id="printeengProfiles">
        <thead>
          <tr>
            <th style={{ width: 50 }}></th>
            <th>Profile</th>
            <th>Quota</th>
            <th>Assigned devices</th>
          </tr>
        </thead>
        <tbody>
          {table.data.data.length === 0 && (
            <tr>
              <td colSpan={4} className="dataTables_empty">
                No data available in table
              </td>
            </tr>
          )}
          {table.data.data.map((row) => (
            <ProfileRowView key={row.ID} row={row} open={expanded === row.ID} onToggle={() => setExpanded(expanded === row.ID ? 0 : row.ID)} companies={page.data.companies} onChanged={reload} />
          ))}
        </tbody>
      </table>
      <ProfileModal open={open} onClose={() => setOpen(false)} companies={page.data.companies} company={page.data.company} onSaved={reload} />
    </div>
  )
}

function ProfileRowView({ row, open, onToggle, companies, onChanged }: { row: ProfileRow; open: boolean; onToggle: () => void; companies: Company[]; onChanged: () => void }) {
  return (
    <>
      <tr id={row.DT_RowId} className={open ? 'shown' : undefined}>
        <td>
          <center className="show_details color-dark-grey pointer font-16" data-id={row.ID} onClick={onToggle}>
            <i className={open ? 'fas fa-minus-circle' : 'fas fa-plus-circle'}></i>
          </center>
        </td>
        <td>{row.name}</td>
        <td>
          <center>
            <b>
              {row.quota} / {row.period_type}
            </b>
          </center>
        </td>
        <td>{row.devices > 0 ? row.devices : <i className="fas fa-minus font-10"></i>}</td>
      </tr>
      {open && (
        <tr className="details-row">
          <td colSpan={4}>
            <div className="row d-flex m-0">
              <div className="col-auto bg-lightest-gray">
                <ul className="action_tabs">
                  <li>
                    <a href="#" className="action_button active" onClick={(e) => e.preventDefault()}>
                      Settings
                    </a>
                  </li>
                </ul>
              </div>
              <div className="col-md-12">
                <div className="details_content">
                  <ProfileSettings id={row.ID} companies={companies} onChanged={onChanged} />
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

/** `profiles/settings.php`: acelasi formular ca la creare, cu Save si Delete. */
function ProfileSettings({ id, companies, onChanged }: { id: number; companies: Company[]; onChanged: () => void }) {
  const [profile, setProfile] = useState<Record<string, unknown> | null>(null)
  useEffect(() => {
    void profileSettings(id).then((r) => setProfile(r.profile))
  }, [id])
  if (!profile) return <Spinner />
  return (
    <div className="row">
      <ProfileForm
        className="form-horizontal profileSettings"
        companies={companies}
        initial={{ name: String(profile.name ?? ''), company: String(profile.company_id ?? ''), quota: String(profile.quota ?? ''), period: String(profile.period ?? '0') }}
        onSubmit={async (fields) => {
          await updateProfile({ ...fields, profile: id })
          toast('Settings changed', 'Success', 'success')
          onChanged()
        }}
        footer={(busy) => (
          <div className="form-actions center">
            <input type="submit" value="Save" className="btn btn-success updateProfile" disabled={busy} />{' '}
            <input
              type="button"
              value="Delete"
              className="btn btn-danger removeProfile"
              data-id={id}
              onClick={() => {
                void removeProfile(id)
                  .then(() => {
                    toast('Profile Deleted!', 'Success', 'success')
                    onChanged()
                  })
                  .catch((err: unknown) => toast(errorMessages(err).join(' '), 'Error', 'error'))
              }}
            />
          </div>
        )}
      />
    </div>
  )
}

/** `profiles/newProfileModal.php`. */
function ProfileModal({ open, onClose, companies, company, onSaved }: { open: boolean; onClose: () => void; companies: Company[]; company: number; onSaved: () => void }) {
  return (
    <Modal id="createProfileModal" open={open} onClose={onClose} title="Create Profile" labelledBy="gridSystemModalLabel" staticBackdrop keyboard={false} footer={null}>
      <ProfileForm
        className="form-horizontal createProfile"
        companies={companies}
        initial={{ name: '', company: String(company), quota: '', period: '0' }}
        onSubmit={async (fields) => {
          await createProfile(fields)
          toast('New profile created', 'Success', 'success')
          onSaved()
          onClose()
        }}
        footer={(busy) => (
          <div className="modal-footer" style={{ margin: '15px -15px -15px' }}>
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>
            <button type="submit" className="btn btn-primary saveProfile" disabled={busy}>
              Save
            </button>
          </div>
        )}
      />
    </Modal>
  )
}

function ProfileForm({
  className,
  companies,
  initial,
  onSubmit,
  footer,
}: {
  className: string
  companies: Company[]
  initial: { name: string; company: string; quota: string; period: string }
  onSubmit: (fields: { name: string; company: string; quota: string; period: string }) => Promise<void>
  footer: (busy: boolean) => React.ReactNode
}) {
  const [fields, setFields] = useState(initial)
  const [busy, setBusy] = useState(false)
  const set = (key: keyof typeof fields, value: string) => setFields({ ...fields, [key]: value })
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    try {
      await onSubmit(fields)
    } catch (err) {
      toast(errorMessages(err).join(' '), 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }
  const label = (text: string) => (
    <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
      <span className="color">{text}</span>
    </label>
  )
  return (
    <form method="post" className={className} onSubmit={(e) => void submit(e)}>
      <div className="form-group">
        {label('Profile Name')}
        <div className="col-sm-6">
          <input type="text" name="name" value={fields.name} className="form-control" required onChange={(e) => set('name', e.target.value)} />
        </div>
      </div>
      <div className="form-group">
        {label('Company')}
        <div className="col-sm-6 selectContainer">
          <select name="company" className="form-control" required value={fields.company} onChange={(e) => set('company', e.target.value)}>
            <option value="">Select</option>
            {companies.map((c) => (
              <option key={c.ID} value={String(c.ID)}>
                {c.Nume}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-group">
        {label('Quota')}
        <div className="col-sm-6">
          <input type="number" name="quota" value={fields.quota} className="form-control" required onChange={(e) => set('quota', e.target.value)} />
        </div>
      </div>
      <div className="form-group">
        {label('Period')}
        <div className="col-sm-6 selectContainer">
          <select name="period" className="form-control" required value={fields.period} onChange={(e) => set('period', e.target.value)}>
            {PERIODS.map(([value, text]) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
          </select>
        </div>
      </div>
      {footer(busy)}
    </form>
  )
}

/** `printeeng_settings/alarms/index.php`. */
function AlarmsPanel() {
  const page = useQuery(moduleAlarmsQuery('/settings/printeeng'))
  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!page.data) return <Spinner />
  return (
    <>
      <h4>Alarms settings</h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Create and edit PRINTEENG <strong>alarms</strong>
      </label>
      <hr />
      <ModuleAlarmsEditor base="/settings/printeeng" categories={page.data.categories} />
    </>
  )
}
